import { getCachedSize, isActiveProfileExplicitlyCleared } from "./utils/storage";
import { jsonHeaders } from "../api-client";
import { getDeviceHint, getOrCreateSessionId } from "../session";
import { setLastSizeSelection } from "../storefront/cart-hook";
import { getApiKey, getApiUrl } from "./utils/locale";
import { detectBodyLandmarks } from "../pose-detect";
import type { Profile, SizingResult, ProfileMeasurements, SizeHistoryEntry } from "./types";
import { logSizeShown } from "../storefront/events";
import { clearStoredProfileSession, getStoredProfileSession } from "./utils/profile-auth";
import { fetchRemoteProfiles, isProfileAuthError, saveRemoteProfiles } from "./utils/remote-profiles";
import { detectProductFitType, productFitTypeToMeasurementType } from "./utils/product-fit";

function shouldUseShopifyProxy(apiUrl: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const w = window as unknown as { Shopify?: { shop?: string } };
    if (w.Shopify?.shop) return true;
    return /\/apps\/primestyle\/?$/.test(apiUrl);
  } catch {
    return false;
  }
}

function reportDirectSdkEvent(apiUrl: string, apiKey: string, body: Record<string, unknown>): void {
  if (!apiKey || shouldUseShopifyProxy(apiUrl)) return;
  void fetch(`${apiUrl}/api/v1/tryon/event`, {
    method: "POST",
    headers: jsonHeaders(apiKey),
    keepalive: true,
    body: JSON.stringify({
      ...body,
      sessionId: getOrCreateSessionId(),
      deviceHint: getDeviceHint(),
    }),
  }).catch(() => {});
}

function isGuideOnlyPlaceholderForSizedProduct(input: RecommendForProductInput, cached: SizeHistoryEntry): boolean {
  if (!/^see available sizes$/i.test((cached.recommendedSize || "").trim())) return false;
  const fitType = detectProductFitType({
    productFitType: input.productFitType,
    productTitle: input.productTitle,
    productCategory: input.productCategory,
    productSubcategory: input.productSubcategory,
    productDescription: input.productDescription,
    productType: input.productType,
    productTags: input.productTags,
  });
  return productFitTypeToMeasurementType(fitType) !== "body-basic";
}

// ── Module-level MediaPipe cache ──
// The recommend flow runs MediaPipe on the FULL-RES uploaded photo. The
// follow-up estimate call (persistResultToProfile) is fired with whatever
// bodyImage is in scope — usually the smaller preview — and re-runs
// MediaPipe at lower resolution, producing different landmarks. That
// divergence makes /recommend and /estimate return different sizes for
// the same body. Cache the recommend-flow result here so the estimate
// call reuses it instead of re-running MediaPipe at lower res.
type CachedMediaPipe = {
  landmarks: any;
  storedAt: number;
};
let cachedMP: CachedMediaPipe | null = null;
const MP_CACHE_TTL_MS = 60_000;

export function setCachedMediaPipe(landmarks: any): void {
  cachedMP = { landmarks, storedAt: Date.now() };
}

function getCachedMediaPipe(): CachedMediaPipe | null {
  if (!cachedMP) return null;
  if (Date.now() - cachedMP.storedAt > MP_CACHE_TTL_MS) {
    cachedMP = null;
    return null;
  }
  return cachedMP;
}

export interface RecommendForProductInput {
  productId: string;
  productTitle: string;
  productImage?: string;
  productCategory?: string;
  productSubcategory?: string;
  productFitType?: string;
  productType?: string;
  productTags?: string[] | string;
  productDescription?: string;
  /** Raw size guide data — pass the same value you'd pass to <PrimeStyleTryon sizeGuideData={...}/> */
  sizeGuideData?: unknown;
  /** Optional explicit profile (otherwise uses the active profile from localStorage) */
  profile?: Profile | null;
  /** Optional API URL override (otherwise reads NEXT_PUBLIC_PRIMESTYLE_API_URL) */
  apiUrl?: string;
  /** Optional API key override (otherwise reads NEXT_PUBLIC_PRIMESTYLE_API_KEY) */
  apiKey?: string;
  /** Skip the localStorage cache (forces a fresh /sizing/recommend call) */
  skipCache?: boolean;
}

export interface RecommendForProductResult {
  /** The suggested size (e.g. "M", "42") */
  recommendedSize: string;
  /** "high" | "medium" | "low" */
  confidence?: string;
  /** For multi-section products: section name → suggested size */
  sections?: Record<string, string>;
  /** The profile this recommendation was computed against */
  profileId: string;
  /** Whether this came from the in-memory cache (no network call) */
  fromCache: boolean;
  /** Full sizing result for downstream use */
  raw?: SizingResult;
  /** True when the backend found a real fit. False when the user's body is
   *  outside every row in the size chart — hosts should suppress their
   *  "Suggested: X" badge and surface a "size unavailable" message. */
  found?: boolean;
}

/**
 * Headless auto-sizing — given a product and an active profile, returns the
 * suggested size WITHOUT opening the SDK modal. Caches results per
 * (profile, product) so repeat visits are instant.
 *
 * Returns null if there is no active profile yet — the host page should
 * fall back to its existing "Find Your Size" CTA which opens the modal.
 */
export async function recommendForProduct(
  input: RecommendForProductInput
): Promise<RecommendForProductResult | null> {
  // Bump a global call counter so the host page can see how many
  // recommendForProduct calls actually fire per page mount.
  const callId = (() => {
    if (typeof window === "undefined") return 0;
    const w = window as any;
    w.__psSdkDiag = w.__psSdkDiag || {};
    w.__psSdkDiag.recommendCalls = (w.__psSdkDiag.recommendCalls || 0) + 1;
    return w.__psSdkDiag.recommendCalls;
  })();
  const log = (...args: unknown[]) => console.log(`[ps-sdk:recommend#${callId}]`, ...args);
  const t0 = Date.now();
  log("ENTER", { productId: input.productId, apiUrl: input.apiUrl, skipCache: !!input.skipCache });
  const sessionId = getOrCreateSessionId();
  const apiKey = input.apiKey ?? getApiKey();
  const apiUrl = (input.apiUrl ?? getApiUrl()).replace(/\/+$/, "");
  const session = getStoredProfileSession();
  if (!session) {
    log("no signed-in profile session — returning null");
    return null;
  }
  if (!input.profile && isActiveProfileExplicitlyCleared()) {
    log("active profile explicitly cleared locally — returning null");
    return null;
  }

  let remoteProfiles: Profile[] = [];
  let activeProfileId: string | null = null;
  try {
    const remoteStore = await fetchRemoteProfiles(apiUrl, session.accessToken);
    remoteProfiles = remoteStore.profiles;
    activeProfileId = remoteStore.activeProfileId;
  } catch (error) {
    if (isProfileAuthError(error)) clearStoredProfileSession();
    log("remote profile fetch failed — returning null", error);
    return null;
  }

  const profile =
    input.profile && input.profile.id === activeProfileId
      ? input.profile
      : activeProfileId
        ? remoteProfiles.find((p) => p.id === activeProfileId) ?? null
        : null;
  if (!profile) {
    log("no selected backend profile — returning null");
    return null;
  }
  log("profile resolved", {
    profileId: profile.id,
    profileName: profile.name,
    hasMeasurements: !!profile.measurements && Object.keys(profile.measurements || {}).length,
    measurementsCount: Object.keys(profile.measurements || {}).length,
  });

  // Honour the cache unless the caller explicitly skipped it
  if (!input.skipCache) {
    const cached = getCachedSize(profile, input.productId);
    if (cached) {
      if (isGuideOnlyPlaceholderForSizedProduct(input, cached)) {
        log("cache STALE guide-only placeholder for sized product — recomputing", {
          recommendedSize: cached.recommendedSize,
          productCategory: input.productCategory,
          productSubcategory: input.productSubcategory,
          productFitType: input.productFitType,
        });
      } else {
        log("cache HIT", { recommendedSize: cached.recommendedSize, hasSectionsFull: !!cached.sectionsFull });
        setLastSizeSelection({
          productId: input.productId,
          productTitle: input.productTitle,
          size: cached.recommendedSize,
          recommendedSize: cached.recommendedSize,
        });
        logSizeShown({
          productId: input.productId,
          productTitle: input.productTitle,
          recommendedSize: cached.recommendedSize,
          fromCache: true,
        });
        reportDirectSdkEvent(apiUrl, apiKey, {
          eventType: "SIZE_RECOMMENDATION_SHOWN",
          productId: input.productId,
          productTitle: input.productTitle,
          recommendedSize: cached.recommendedSize,
          metadata: { fromCache: true, profileId: profile.id },
        });
        // Reconstruct a SizingResult-shaped `raw` from the cached entry so
        // hosts that rely on raw.sections (with size/length per section)
        // get the same shape they'd get from a fresh /sizing/recommend call.
        const reconstructedRaw: SizingResult | undefined = cached.sectionsFull
          ? ({
              recommendedSize: cached.recommendedSize,
              confidence: (cached.confidence as any) || "high",
              reasoning: "",
              recommendedLength: cached.recommendedLength,
              sections: Object.fromEntries(
                Object.entries(cached.sectionsFull).map(([name, sec]) => [
                  name,
                  { ...sec, matchDetails: [] } as any,
                ])
              ),
            } as SizingResult)
          : ({
              recommendedSize: cached.recommendedSize,
              confidence: (cached.confidence as any) || "high",
              reasoning: "",
              recommendedLength: cached.recommendedLength,
            } as SizingResult);
        return {
          recommendedSize: cached.recommendedSize,
          confidence: cached.confidence,
          sections: cached.sections,
          profileId: profile.id,
          fromCache: true,
          raw: reconstructedRaw,
        };
      }
    }
  }

  log(`cache MISS — calling backend (elapsed in pre-flight: ${Date.now() - t0}ms)`);

  // 1. Normalize the size guide via /sizing/sizeguide (cache-friendly on backend)
  let sizeGuide: unknown = null;
  if (input.sizeGuideData != null) {
    const tSg = Date.now();
    log("→ POST /api/v1/sizing/sizeguide");
    try {
      const sgRes = await fetch(`${apiUrl}/api/v1/sizing/sizeguide`, {
        method: "POST",
        headers: jsonHeaders(apiKey),
        body: JSON.stringify({
	    product: {
	      title: input.productTitle,
	      productId: input.productId,
	      productImage: input.productImage,
	      category: input.productCategory,
            subcategory: input.productSubcategory,
            productFitType: input.productFitType,
            productType: input.productType,
            tags: Array.isArray(input.productTags)
              ? input.productTags
              : input.productTags
                ? input.productTags.split(",").map((tag) => tag.trim()).filter(Boolean)
                : [],
            description: input.productDescription || "",
          },
          sizeGuideRaw: input.sizeGuideData,
        }),
      });
      if (sgRes.ok) {
        sizeGuide = await sgRes.json();
        log(`← sizeguide OK in ${Date.now() - tSg}ms`, { found: (sizeGuide as any)?.found, sectionCount: Object.keys((sizeGuide as any)?.sections || {}).length });
      } else {
        log(`← sizeguide FAILED in ${Date.now() - tSg}ms`, sgRes.status, sgRes.statusText);
      }
    } catch (e) {
      log(`← sizeguide threw in ${Date.now() - tSg}ms`, e);
    }
  } else {
    log("no sizeGuideData provided — skipping /sizing/sizeguide");
  }

  // 2. Build the recommend payload from the profile's stored measurements.
  // Profile measurements are unit-tagged so we send them in the unit they
  // were computed in (the backend converts internally for the matcher).
  const measurements: Record<string, unknown> = {
    gender: profile.gender,
    sizingUnit: profile.measurementsUnit || "cm",
  };
  if (profile.measurements) {
    for (const [key, value] of Object.entries(profile.measurements)) {
      if (value != null) measurements[key] = value;
    }
  }
  const legacyKnownMeasurements: Record<string, number> = {};
  for (const key of [
    "waist", "hips", "chest", "bust", "shoulderWidth", "sleeveLength",
    "inseam", "neckCircumference", "thighCircumference", "wristCircumference",
    "footLengthCm",
  ]) {
    const value = (profile as any)[key];
    if (typeof value === "number" && value > 0) {
      measurements[key] = value;
      legacyKnownMeasurements[key] = value;
    }
  }
  if (profile.customMeasurements) {
    for (const [key, value] of Object.entries(profile.customMeasurements)) {
      if (typeof value === "number" && value > 0) {
        measurements[key] = value;
        legacyKnownMeasurements[key] = value;
      }
    }
  }
  // Also include height/weight in case the backend needs them as anchors
  if (profile.height != null) measurements.height = profile.height;
  if (profile.weight != null) measurements.weight = profile.weight;
  if (profile.heightUnit) measurements.heightUnit = profile.heightUnit;
  if (profile.weightUnit) measurements.weightUnit = profile.weightUnit;
  if (profile.age) measurements.age = profile.age;
  if (profile.chestProfile) measurements.chestProfile = profile.chestProfile;
  if (profile.midsectionProfile) measurements.midsectionProfile = profile.midsectionProfile;
  if (profile.hipProfile) measurements.hipProfile = profile.hipProfile;

  const payload: Record<string, unknown> = {
    sessionId,
    method: "exact",
    locale: profile.country || "US",
    sizingUnit: profile.measurementsUnit || profile.sizingUnit || "cm",
    product: {
      title: input.productTitle,
      productId: input.productId,
      category: input.productCategory,
      subcategory: input.productSubcategory,
      productFitType: input.productFitType,
      productType: input.productType,
      tags: Array.isArray(input.productTags)
        ? input.productTags
        : input.productTags
          ? input.productTags.split(",").map((tag) => tag.trim()).filter(Boolean)
          : [],
      description: input.productDescription || "",
      variants: [],
    },
    measurements,
  };
  // Bra size (item #13): convert SDK band/cup → backend braSize shape.
  if (profile.gender === "female" && (profile as any).bandSize && (profile as any).cupSize) {
    const region = ((profile as any).braRegion ?? (profile as any).braSizeRegion ?? "US") as
      "US" | "UK" | "EU" | "FR" | "IT" | "JP" | "KR" | "AU";
    payload.braSize = {
      band: parseFloat(String((profile as any).bandSize)),
      cup: String((profile as any).cupSize),
      region,
    };
  }
  // Manual measurement overrides (item #12): if profile carries known
  // measurements (e.g. user-entered chest/waist/hips), forward them so
  // the backend treats them as ground truth.
  if ((profile as any).knownMeasurements) {
    payload.knownMeasurements = (profile as any).knownMeasurements;
  } else if (Object.keys(legacyKnownMeasurements).length > 0) {
    payload.knownMeasurements = legacyKnownMeasurements;
  }
  if (sizeGuide && (sizeGuide as { found?: boolean }).found) {
    payload.sizeGuide = sizeGuide;
  }

  // 3. Call /sizing/recommend
  log("→ POST /api/v1/sizing/recommend", { measurements: Object.keys(measurements), hasSizeGuide: !!payload.sizeGuide });
  const tRec = Date.now();
  let result: SizingResult | null = null;
  try {
    const res = await fetch(`${apiUrl}/api/v1/sizing/recommend`, {
      method: "POST",
      headers: jsonHeaders(apiKey),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      log(`← recommend FAILED in ${Date.now() - tRec}ms`, res.status, res.statusText);
      return null;
    }
    result = await res.json();
    log(`← recommend OK in ${Date.now() - tRec}ms (total fn ${Date.now() - t0}ms)`, {
      recommendedSize: result?.recommendedSize,
      sectionKeys: result?.sections ? Object.keys(result.sections) : null,
      sections: result?.sections,
    });
  } catch (e) {
    log(`← recommend threw in ${Date.now() - tRec}ms`, e);
    return null;
  }
  if (!result || !result.recommendedSize) {
    log("recommend returned no recommendedSize — bailing");
    return null;
  }

  // 4. Save to history (per-product cache).
  // Persist BOTH the legacy flat shape (for older readers) and the full
  // per-section data (for hosts that need size/length to populate
  // dropdowns from the cache).
  const sectionsMap: Record<string, string> | undefined = result.sections
    ? Object.fromEntries(
        Object.entries(result.sections).map(([name, sec]) => [name, sec.recommendedSize])
      )
    : undefined;
  const sectionsFull = result.sections
    ? Object.fromEntries(
        Object.entries(result.sections).map(([name, sec]) => [
          name,
          {
            recommendedSize: sec.recommendedSize,
            // Backend may include these on merged Jacket/Pants entries
            size: (sec as any).size,
            length: (sec as any).length,
            availableSizes: (sec as any).availableSizes,
            availableLengths: (sec as any).availableLengths,
          },
        ])
      )
    : undefined;

  const newHistoryEntry: SizeHistoryEntry = {
    productId: input.productId,
    productTitle: input.productTitle,
    productImage: input.productImage,
    recommendedSize: result.recommendedSize,
    confidence: result.confidence,
    sections: sectionsMap,
    sectionsFull,
    recommendedLength: result.recommendedLength || undefined,
    savedAt: Date.now(),
  };
  const updatedProfiles = remoteProfiles.map((p) => {
    if (p.id !== profile.id) return p;
    const history = (p.sizeHistory || []).filter((h) => h.productId !== input.productId);
    history.unshift(newHistoryEntry);
    return { ...p, sizeHistory: history.slice(0, 50), lastUsedAt: Date.now() };
  });
  void saveRemoteProfiles(apiUrl, session.accessToken, updatedProfiles, activeProfileId).catch((error) => {
    if (isProfileAuthError(error)) clearStoredProfileSession();
    log("remote size-history save failed", error);
  });
  setLastSizeSelection({
    productId: input.productId,
    productTitle: input.productTitle,
    size: result.recommendedSize,
    recommendedSize: result.recommendedSize,
  });
  logSizeShown({
    productId: input.productId,
    productTitle: input.productTitle,
    recommendedSize: result.recommendedSize,
    fromCache: false,
  });
  reportDirectSdkEvent(apiUrl, apiKey, {
    eventType: "SIZE_RECOMMENDATION_SHOWN",
    productId: input.productId,
    productTitle: input.productTitle,
    recommendedSize: result.recommendedSize,
    metadata: { fromCache: false, profileId: profile.id, found: result.found },
  });

  return {
    recommendedSize: result.recommendedSize,
    confidence: result.confidence,
    sections: sectionsMap,
    profileId: profile.id,
    fromCache: false,
    raw: result,
    found: result.found,
  };
}

/**
 * Convenience: estimate full body measurements via /sizing/estimate so they
 * can be persisted to a profile. Used internally when the user finishes the
 * body profile flow — gives us measurements for ANY future product.
 */
export async function estimateFullMeasurements(args: {
  apiUrl?: string;
  apiKey?: string;
  height?: number;
  weight?: number;
  heightUnit: "cm" | "in" | "ft";
  weightUnit: "kg" | "lbs";
  gender: "male" | "female";
  age?: number;
  chestProfile?: string;
  midsectionProfile?: string;
  hipProfile?: string;
  bandSize?: string;
  cupSize?: string;
  braSizeRegion?: string;
  braRegion?: string;
  bodyImage?: string;
  bodyLandmarks?: Record<string, { x: number; y: number }> | null;
}): Promise<{
  estimates: ProfileMeasurements;
  unit: "cm" | "in";
  userEstimates?: { height?: number; weight?: number; age?: number };
} | null> {
  const apiKey = args.apiKey ?? getApiKey();
  const apiUrl = (args.apiUrl ?? getApiUrl()).replace(/\/+$/, "");

  // Ask for ALL the measurements any garment might need.
  const requiredFields = [
    "waist", "hips", "chest", "bust",
    "shoulderWidth", "sleeveLength", "inseam",
    "neckCircumference", "thighCircumference",
    "footLengthCm",
  ];

  const payload: Record<string, unknown> = {
    height: args.height ?? 0,
    weight: args.weight ?? 0,
    heightUnit: args.heightUnit,
    weightUnit: args.weightUnit,
    gender: args.gender,
    requiredFields,
  };
  if (args.age) payload.age = args.age;
  if (args.chestProfile) payload.chestProfile = args.chestProfile;
  if (args.midsectionProfile) payload.midsectionProfile = args.midsectionProfile;
  if (args.hipProfile) payload.hipProfile = args.hipProfile;
  if (args.gender === "female" && args.bandSize && args.cupSize) {
    payload.braSize = {
      band: parseFloat(String(args.bandSize)),
      cup: String(args.cupSize),
      region: args.braSizeRegion || args.braRegion || "US",
    };
  }
  if (args.bodyImage) payload.bodyImage = args.bodyImage;

  // ── MediaPipe auto-detect (with cache) ──
  // Backend's cm-anchor block requires bodyLandmarks. Cache hit avoids
  // re-running MediaPipe at a different image resolution than /recommend
  // used (which would produce different anchor scale → drift).
  let landmarksForPayload = args.bodyLandmarks ?? null;
  if (!landmarksForPayload) {
    const cached = getCachedMediaPipe();
    if (cached) {
      landmarksForPayload = cached.landmarks;
      console.log("[PS-SDK:estimate] reusing cached MediaPipe result from recent /recommend run");
    }
  }
  if (!landmarksForPayload && args.bodyImage) {
    console.log("[PS-SDK:estimate] bodyImage present but bodyLandmarks missing — running MediaPipe now");
    try {
      const detected = await detectBodyLandmarks(args.bodyImage);
      if (detected) {
        landmarksForPayload = detected as unknown as Record<string, { x: number; y: number }>;
        console.log("[PS-SDK:estimate] MediaPipe auto-detect ✓ anchors available for backend");
      } else {
        console.warn("[PS-SDK:estimate] MediaPipe auto-detect returned null — backend will fall back to low-confidence visual estimation");
      }
    } catch (e) {
      console.warn("[PS-SDK:estimate] MediaPipe auto-detect threw:", e);
    }
  }
  if (landmarksForPayload) payload.bodyLandmarks = landmarksForPayload;
  // Local pre-submit pose check (item #10): if any required torso joint has
  // visibility < 0.5, log a warning. Doesn't block — backend gatekeeper +
  // confidence flag will handle it. This is just a fast client-side hint.
  if (landmarksForPayload) {
    const lm = landmarksForPayload as any;
    const required: Array<[string, number]> = [
      ["leftShoulder", lm.leftShoulder?.visibility],
      ["rightShoulder", lm.rightShoulder?.visibility],
      ["leftHip", lm.leftHip?.visibility],
      ["rightHip", lm.rightHip?.visibility],
    ];
    const lowVis = required.filter(([, v]) => typeof v === "number" && v < 0.5);
    if (lowVis.length > 0) {
      console.warn(`[PS-SDK:estimate] LOW joint visibility — ${lowVis.map(([k, v]) => `${k}=${(v as number).toFixed(2)}`).join(", ")} — accuracy may suffer; ask user to retake with better framing/lighting`);
    }
  }

  try {
    // Verbose pipeline log — makes the /sizing/estimate request inspectable
    // end-to-end in DevTools. Grouped so it collapses neatly.
    console.groupCollapsed(`[PS-SDK:estimate] POST /api/v1/sizing/estimate`);
    console.log("url:", `${apiUrl}/api/v1/sizing/estimate`);
    console.log("payload (sans bodyImage):", {
      ...payload,
      bodyImage: payload.bodyImage ? `[base64 ~${Math.round(String(payload.bodyImage).length / 1024)}KB]` : undefined,
      bodyLandmarks: payload.bodyLandmarks
        ? {
            count: Object.keys(payload.bodyLandmarks as object).length,
            imageWidth: (payload.bodyLandmarks as any).imageWidth,
            imageHeight: (payload.bodyLandmarks as any).imageHeight,
          }
        : undefined,
    });
    console.groupEnd();
    {
      const lmObj = payload.bodyLandmarks as Record<string, unknown> | undefined;
      const jointCount = lmObj
        ? Object.values(lmObj).filter(
            (v) => v && typeof v === "object" && typeof (v as any).x === "number" && typeof (v as any).y === "number",
          ).length
        : 0;
      console.log(`[ps-sdk:debug] /estimate payload → bodyLandmarks=${!!lmObj}(${jointCount} joints)`);
    }
    const res = await fetch(`${apiUrl}/api/v1/sizing/estimate`, {
      method: "POST",
      headers: jsonHeaders(apiKey),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      console.warn(`[PS-SDK:estimate] ✗ ${res.status} ${res.statusText}`);
      return null;
    }
    const data = await res.json();
    console.groupCollapsed(`[PS-SDK:estimate] ✓ response (confidence=${data?.confidence ?? "—"})`);
    console.log("estimates:", data.estimates);
    console.log("userEstimates:", data.userEstimates);
    console.log("method:", data.method, "unit:", data.unit);
    console.log("full response:", data);
    console.groupEnd();
    if (!data?.estimates) return null;
    return {
      estimates: data.estimates as ProfileMeasurements,
      unit: (data.unit || "cm") as "cm" | "in",
      userEstimates: data.userEstimates,
    };
  } catch (err) {
    console.error("[PS-SDK:estimate] threw:", err);
    return null;
  }
}
